// src/tattoo/CopyLinkButton.jsx
import React from "react";

function isAppleMobile() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const iOS = /iPad|iPhone|iPod/.test(ua);
  // iPadOS 13+ can report as Macintosh with touch
  const iPadOS13Plus =
    /Macintosh/.test(ua) && typeof navigator.maxTouchPoints === "number" && navigator.maxTouchPoints > 1;
  return iOS || iPadOS13Plus;
}

async function safeCopy(text) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {}
  // Fallback textarea (for older desktop browsers)
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "-9999px";
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
    return true;
  } catch {}
  return false;
}

/**
 * Props:
 * - makeUrl: () => Promise<string>   // must resolve to final URL
 * - label?: string                   // optional override; otherwise auto-labels
 * - className?: string
 */
export default function CopyLinkButton({ makeUrl, label, className = "" }) {
  const [busy, setBusy] = React.useState(false);
  const [ok, setOk] = React.useState(false);
  const [err, setErr] = React.useState("");

  const canNativeShare = typeof navigator !== "undefined" && typeof navigator.share === "function" && isAppleMobile();

  const dynamicLabel = label || (canNativeShare ? "Share…" : "Copy link");

  const onClick = async () => {
    if (busy) return;
    setBusy(true);
    setErr("");
    try {
      const url = await makeUrl();
      if (!url || typeof url !== "string") {
        throw new Error("No link generated");
      }

      if (canNativeShare) {
        // iOS/iPadOS native share sheet
        try {
          await navigator.share({
            title: "Tattune share",
            text: "Check out this design review link:",
            url,
          });
          // No “copied” state needed; iOS shows the sheet
          setOk(false);
        } catch (e) {
          // If the user cancels share, silently fall back to copy so they’re not stuck
          const copied = await safeCopy(url);
          setOk(copied);
          if (!copied) setErr("Couldn’t share or copy");
        }
      } else {
        // Desktop/other: copy to clipboard with checkmark feedback
        const copied = await safeCopy(url);
        setOk(copied);
        if (!copied) setErr("Copy failed");
      }
    } catch (e) {
      setErr(e?.message || "Couldn’t build link");
      setOk(false);
    } finally {
      setBusy(false);
      if (!canNativeShare && !err) {
        // Flash the check for ~1.2s on desktop
        if (ok) {
          const t = setTimeout(() => setOk(false), 1200);
          return () => clearTimeout(t);
        }
      }
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className={`btn ${className}`}
      disabled={busy}
      aria-live="polite"
      title={canNativeShare ? "Share…" : "Copy link"}
      style={{ position: "relative", minWidth: 120 }}
    >
      {busy ? "…" : ok ? "✅ Copied" : dynamicLabel}
      {err && (
        <span
          style={{
            position: "absolute",
            bottom: -18,
            left: 0,
            right: 0,
            fontSize: 11,
            color: "#ef4444",
          }}
        >
          {err}
        </span>
      )}
    </button>
  );
}